import React, { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  ZoomControl,
  Marker,
  AttributionControl,
  useMapEvent,
  Popup,
} from "react-leaflet";
import { Link, Text } from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import MarkerClusterGroup from "react-leaflet-markercluster";
import { distanceBetween } from "geofire-common";
import { SubmitFormModal, HoopsModal } from "../views";
import { SearchBar, Geolocator, Layers, CenteredSpinner } from "../components";
import config from "./config";
import { db } from "../api/firebase";
import { getMarkerIcon, parseLocationAreaName } from "../utils";

const App = () => {
  const [ isLoading, setIsLoading ] = useState(false);
  const [ activeFetchType, _setActiveFetchType ] = useState("default");
  const [ markers, setMarkers ] = useState([]);
  const [ hoops, setHoops ] = useState({
    modalOpen: false,
    cluster: [],
    clusterMeta: {},
  });
  const [ autoOpenSubmitForm, setAutoOpenSubmitForm ] = useState(false);
  const [ submitFormIsOpen, setSubmitFormIsOpen ] = useState(false);
  const [ submissionLocationDefaults, setSubmissionLocationDefaults ] = useState(null);
  const markerClusterRef = useRef();

  const fetchData = async (fetchType = 'default') => {
    setIsLoading(true);
    const markerData = [];

    const submissionsRef = db.collection("submissions");
    const halfcourtHoopsRef = submissionsRef.where('isHalfcourtHoop', '==', true);
    const redbullEventsRef = db.collection("redbullEvents");

    if (fetchType === 'default') {
      const submissionDocs = await submissionsRef.get();
      submissionDocs.forEach((doc) => {
        const document = { ...doc.data(), id: doc.id };
        markerData.push(document);
      });
    } else if (fetchType === 'redbull') {
      const eventDocs = await redbullEventsRef.get();
      const halfcourtHoopDocs = await halfcourtHoopsRef.get();

      halfcourtHoopDocs.forEach((doc) => {
        const document = { ...doc.data(), id: doc.id };
        markerData.push(document);
      });
      eventDocs.forEach((doc) => {
        const document = { ...doc.data(), id: doc.id };
        markerData.push(document);
      });
    }

    setMarkers(markerData);
    setIsLoading(false);
  };

  const ZoomChangeHandler = () => {
    useMapEvent("zoomend", (event) => {
      const { _zoom } = event.target;
      const { _featureGroup } = markerClusterRef.current;
      const clusters = Object.values(_featureGroup._layers);

      clusters.forEach((cluster) => {
        const isMarker = cluster._icon;

        if (isMarker && activeFetchType === "default") {
          if (_zoom === config.map.defaultZoomLevel) {
            cluster.refreshIconOptions({ className: "hidden-marker" });
          } else {
            cluster.refreshIconOptions({ className: "" });
          }
        }
      });
      markerClusterRef.current.refreshClusters();
    });
    return null;
  };

  useEffect(() => {
    if (markers.length === 0) {
      fetchData();
    }
  }, [ markers.length ]);

  const clearActiveHoops = () => {
    setHoops({ modalOpen: false, cluster: [], clusterMeta: {} });
  };

  const onClickMarker = ({ target }) => {
    if (target?.__parent) {
      const clusterChildren = target.__parent.getAllChildMarkers();
      const targetCenter = target.getLatLng();

      const clusterGroupData = clusterChildren
        .filter((m) => {
          // filter hoops by their distance from the selected marker
          const distanceInKm = distanceBetween(
            [ m._latlng.lat, m._latlng.lng ],
            [ targetCenter.lat, targetCenter.lng ],
          );
          const isWithinRange = distanceInKm <= config.map.maxClusterDistanceInKm;
          return isWithinRange;
        })
        .sort((_a, b) => (b.options.id === target.options.id ? 1 : -1))
        .map((m) => ({ ...m.options, position: m.getLatLng() }));

      setHoops({
        modalOpen: true,
        cluster: clusterGroupData,
        clusterMeta: {
          formatted_address: target.options.location.formatted_address,
          locationAreaName: parseLocationAreaName(target.options.location),
          country: target.options.location.country,
        },
      });
    } else { // not a cluster
      setHoops({
        modalOpen: true,
        cluster: [ { ...target.options, position: target.getLatLng() } ],
        clusterMeta: {
          formatted_address: target.options.location.formatted_address,
          locationAreaName: parseLocationAreaName(target.options.location),
          country: target.options.location.country,
        },
      });
    }
  };

  const onClusterClick = ({ layer }) => {
    const { _zoom: currentZoom } = layer;

    if (currentZoom < config.map.maxZoomLevel) {
      layer.unspiderfy();
      layer.zoomToBounds({ maxZoom: config.map.maxZoomLevel });
    } else {
      layer.spiderfy();
    }
  };

  const renderMarkers = () => {
    if (isLoading) return null;

    return markers.map((marker) => (
      <Marker
        key={marker.id}
        id={marker.id}
        alt={marker.location.formatted_address}
        location={marker.location}
        caption={marker.caption}
        contributor={marker.contributor}
        brand={marker.brand}
        image={marker.image}
        icon={getMarkerIcon(marker.brand, activeFetchType, marker.isRedbullEvent, marker.isHalfcourtHoop, markerClusterRef)}
        position={{
          lat: marker.location.coords.latitude,
          lng: marker.location.coords.longitude,
        }}
        eventHandlers={{ click: marker.isRedbullEvent ? () => null : onClickMarker }}
        riseOnHover
        autoPan
      >
        {marker.isRedbullEvent && (
          <Popup className="redbull-event">
            <Link fontSize="sm" href="https://www.redbull.com/us-en/event-series/3x" isExternal>
              Red Bull 3X Qualifier Learn More
              <ExternalLinkIcon mx="4px" />
            </Link>
            <hr />
            <Text fontSize="md" my="12px !important">
              {`${marker.caption}`}
            </Text>
          </Popup>
        )}
      </Marker>
    ));
  };

  return (
    <MapContainer
      id="primary-map"
      center={config.map.defaultCenter}
      zoom={config.map.defaultZoomLevel}
      minZoom={
        navigator.maxTouchPoints > 1
          ? config.map.minZoomLevel
          : config.map.defaultZoomLevel
      }
      maxZoom={config.map.maxZoomLevel}
      maxBounds={config.map.maxBoundsWorld}
      worldCopyJump
      maxBoundsViscosity={1.0}
      zoomControl={false}
      boxZoom={false}
      bounceAtZoomLimits={false}
      attributionControl={false}
      tap={false}
    >
      {config.map.enableAttribution && (
        <AttributionControl position="bottomleft" />
      )}

      {isLoading && <CenteredSpinner />}

      <Layers position="bottomleft" />

      <ZoomChangeHandler />

      <MarkerClusterGroup
        ref={markerClusterRef}
        showCoverageOnHover={false}
        zoomToBoundsOnClick={false}
        spiderfyOnMaxZoom={false}
        // use 5 for disabling clusters after 2 zooms
        // OR 13 to retain clusters at deeper zoom (i.e for handling NYC overlaps)
        disableClusteringAtZoom={13}
        maxClusterRadius={(zoom) => {
          if (activeFetchType === "redbull") return 1;
          return zoom < 5 ? 45 : 100;
        }}
        animate={false}
        chunkedLoading
        removeOutsideVisibleBounds
        spiderLegPolylineOptions={{
          color: "var(--color-brand-red)",
          weight: 1,
        }}
        onClick={onClusterClick}
      >
        {activeFetchType !== "redbull" && renderMarkers()}
      </MarkerClusterGroup>

      {activeFetchType === "redbull" && renderMarkers()}

      <HoopsModal
        hoops={hoops}
        onAfterClose={clearActiveHoops}
        onAddToExistingHoop={({ location }) => {
          clearActiveHoops();
          setAutoOpenSubmitForm(true);
          setSubmissionLocationDefaults({
            location: {
              ...location,
              found: true,
              latitude: location.coords.latitude,
              longitude: location.coords.longitude,
            },
          });
        }}
      />

      <SubmitFormModal
        position="bottomcenter"
        handleSubmitSuccess={fetchData}
        defaultOpen={autoOpenSubmitForm && submissionLocationDefaults}
        onAfterOpen={() => setSubmitFormIsOpen(true)}
        onAfterClose={() => {
          setSubmitFormIsOpen(false);
          setSubmissionLocationDefaults(null);
        }}
        defaultHoopLocation={submissionLocationDefaults}
        defaultIsHalfcourt={activeFetchType === 'redbull'}
      />

      {!submitFormIsOpen && !hoops.modalOpen && (
        <>
          <SearchBar />
          <Geolocator
            position="quarterbottomright"
            zoomTo={config.map.repositionedZoomLevel}
          />
          <ZoomControl position="bottomright" />
        </>
      )}
    </MapContainer>
  );
};

export default App;
